import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import useFetch from "../../hooks/useFetch";

const SHGMembersPage = () => {
  const authState = useSelector((s) => s.authReducer);
  const navigate = useNavigate();
  const location = useLocation();
  const [fetchData] = useFetch();

  const params = new URLSearchParams(location.search);
  const shgCode = params.get("shgId");
  const shgName = params.get("shgName");

  const userId = authState?.user?._id;
  const token = authState?.token;

  const emptyForm = {
    shgCode: shgCode || "",
    shgName: shgName || "",
    shgMemberThink5whysCode: "",
    shgMemberName: "",
    shgMemberType: "",
    memberSince: new Date().toISOString().substring(0, 10),
  };

  const [form, setForm] = useState(emptyForm);
  const [members, setMembers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [msg, setMsg] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState(false);
  const [referralInfo, setReferralInfo] = useState({ name: "", email: "", place: "" });
  const [error, setError] = useState("");

  // Load existing members
  const loadMembers = useCallback(() => {
    fetchData({
      url: "/shgmembers/list",
      method: "post",
      headers: { Authorization: token },
      data: { shgCode },
    }).then((res) => {
      if (res?.status) setMembers(res.members);
    });
  }, [fetchData, shgCode, token]);

  useEffect(() => {
    if (!shgCode) return;
    loadMembers();
  }, [shgCode, loadMembers]);

  // Validate & verify Think5Whys code
  const fetchMemberThink5WhysCode = useCallback(
    (code) => {
      const regex = /^[A-Z0-9]{14}$/;
      if (!regex.test(code)) {
        setError("Please enter a valid 14-character alphanumeric Think5Whys Code.");
        setValidationStatus(false);
        return;
      }

      setIsValidating(true);
      setError("");

      fetchData({
        url: "/auth/verifyreferralcode",
        method: "post",
        headers: { Authorization: authState.token },
        data: { think5whyscode: code },
      })
        .then((res) => {
          if (res?.status) {
            setReferralInfo({
              name: res.t5wcode.name,
              email: res.t5wcode.email,
              place: res.t5wcode.place || "Not specified",
            });
            setForm((prev) => ({
              ...prev,
              shgMemberThink5whysCode: code,
              shgMemberName: res.t5wcode.name,
            }));
            setValidationStatus(true);
            setMsg(`Code ${code} verified! 😊`);
          } else {
            setReferralInfo({ name: "", email: "", place: "" });
            setValidationStatus(false);
            setMsg(`Code ${code} not found! 😔`);
          }
        })
        .catch((err) => {
          console.error(err);
          setValidationStatus(false);
          setMsg(`Verification failed! 😔`);
        })
        .finally(() => setIsValidating(false));
    },
    [fetchData, token]
  );

  const handleSave = () => {
    if (!form.shgMemberName) {
      setMsg("Member Name required");
      return;
    }

    const url = editId ? "/shgmembers/update" : "/shgmembers/create";
    const payload = editId
      ? { memberId: editId, SHGMemberData: form }
      : { ...form, createdBy: userId };

    fetchData({
      url,
      method: "post",
      headers: { Authorization: authState.token },
      data: payload,
    }).then((res) => {
      if (res?.status) {
        setMsg(editId ? "Updated!" : "Created!");
        setForm(emptyForm);
        setEditId(null);
        loadMembers();
        setValidationStatus(false);
        setReferralInfo({ name: "", email: "", place: "" });
      }
    });
  };

  const handleEdit = (id) => {
    fetchData({
      url: "/shgmembers/details",
      method: "post",
      headers: { Authorization: authState.token },
      data: { memberId: id },
    }).then((res) => {
      if (res?.status && res.member) {
        setForm(res.member);
        setEditId(id);
      }
    });
  };

  const handleDelete = (id) => {
    fetchData({
      url: "/shgmembers/update",
      method: "post",
      headers: { Authorization: authState.token },
      data: { memberId: id, SHGMemberData: { isDeleted: true, isActive: false } },
    }).then(() => loadMembers());
  };

  return (
    <div className="bg-black min-h-screen text-white p-4">
      <h1 className="text-xl font-bold text-pink-400 mb-4">
        Members of {shgCode} : {shgName}
      </h1>

      <div className="bg-gray-800 p-4 rounded-xl border border-gray-700 mb-6">
        <h2 className="text-lg font-bold text-purple-300 mb-3">
          {editId ? "Edit Member" : "Add New Member"}
        </h2>

        <div className="flex flex-col gap-2">
          {Object.keys(emptyForm).map((key) => {
            const disabledFields = ["shgCode", "shgName"];
            const alwaysDisabledFields = ["shgMemberName"]; 
            const isThink5WhysCode = key === "shgMemberThink5whysCode";

            // Decide if the input should be disabled
            const isDisabled =
              disabledFields.includes(key) ||
              (alwaysDisabledFields.includes(key) && validationStatus);

            return (
              <div key={key} className="flex items-center gap-2">
                <input
                  name={key}
                  value={
                    key === "shgMemberName" && validationStatus
                      ? referralInfo.name
                      : form[key] || ""
                  }
                  // onChange={(e) => setForm({ ...form, [key]: e.target.value.toUpperCase() })}
                  onChange={(e) =>
                    setForm({ ...form, [key]: e.target.value ? e.target.value.toUpperCase() : "" })
                  }
                  placeholder={key}
                  disabled={isDisabled}
                  className={`p-2 rounded bg-gray-700 border border-gray-600 ${
                    isDisabled ? "text-gray-400" : ""
                  }`}
                  maxLength={isThink5WhysCode ? 14 : undefined}
                />

                {isThink5WhysCode && (
                  <button
                    onClick={() => {
                      const code = form[key] ? form[key].toUpperCase() : "";
                      if (code) fetchMemberThink5WhysCode(code);
                    }}
                    disabled={isValidating || !form[key]}
                    className="bg-blue-600 w-20 px-3 py-1 rounded mt-4"
                  >
                    Verify
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {validationStatus && (
          <div className="text-green-400 mt-2">
            {`Name: ${referralInfo.name}, Email: ${referralInfo.email}, Place: ${referralInfo.place}`}
          </div>
        )}
        {error && <p className="text-red-400 mt-2">{error}</p>}
        {msg && <p className="text-yellow-300 mt-2">{msg}</p>}

        <div className="flex gap-3 mt-4">
          <button onClick={handleSave} className="bg-pink-600 px-4 py-2 rounded">
            {editId ? "Update" : "Create"}
          </button>

          {editId && (
            <button
              onClick={() => {
                setEditId(null);
                setForm(emptyForm);
              }}
              className="bg-gray-500 px-4 py-2 rounded"
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* MEMBER LIST */}
      <div className="bg-gray-900 p-4 rounded-xl border border-gray-700">
        <h2 className="text-lg font-bold text-purple-400 mb-3">Members</h2>

        {members.length === 0 ? (
          <p className="text-gray-400">No Members Found.</p>
        ) : (
          <div className="space-y-3">
            {members.map((m) => (
              <div
                key={m._id}
                className="p-3 bg-gray-800 rounded border border-gray-700 flex justify-between"
              >
                <div>
                  <p className="text-yellow-300 font-semibold">{m.shgMemberName}</p>
                  <p className="text-gray-400 text-sm">Type: {m.shgMemberType}</p>
                  <p className="text-gray-400 text-sm">Membership Date: {m.memberSince}</p>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => handleEdit(m._id)}
                    className="w-8 h-8 flex justify-center items-center bg-blue-600 rounded-full"
                  >
                    <i className="fas fa-edit" />
                  </button>

                  <button
                    onClick={() => handleDelete(m._id)}
                    className="w-8 h-8 flex justify-center items-center bg-red-600 rounded-full"
                  >
                    <i className="fas fa-trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SHGMembersPage;
